/**
 * Chat Page - Uses desktop ChatView component
 *
 * Full-featured chat interface with:
 * - Message history
 * - File attachments
 * - Screenshots
 * - Streaming responses
 * - Session management
 */

import React from 'react';
import type { PromptTemplate } from '@ctrl-enter/types';
import { createLogger } from '@ctrl-enter/tools/utils';
import { BugReportModal } from '../components/bug-report-modal';
import { PromptLibraryModal } from '../components/prompt-library-modal';
import { PromptFormModal } from '../components/prompt-form-modal';
import { ShortcutTipCard } from '../components/shortcut-tip-card';
import { ShortcutsCheatSheetModal } from '../components/shortcuts-cheat-sheet-modal';
import { Toolbar } from '../components/toolbar';
import { EnterTasksView } from '../components/enter-tasks-view';
import { useRealTimeModeMatch } from '../stores/real-time-mode-store';
import { ChatAvailabilityProvider } from '../services/chat-availability-service';
import { ChatView, type ChatViewRef } from '../views/chat-view/chat-view';
import { SettingsView, isSettingsSection, type SettingsSection } from '../views/settings-view';
import { usePromptLibraryIntegration } from '../hooks/use-prompt-library-integration';
import {
  useCreateUserPromptMutation,
  useUpdateUserPromptMutation,
} from '../hooks/use-gateway-queries';
import { usePromptLibraryStore } from '../stores/prompt-library-store';
import { useShowOnboarding } from '../stores/personalization-store';
import { OnboardingFlow } from '../components/onboarding-flow';
import { toast } from '../utils/toast';
import { usePlatform } from '../contexts/platform-context';
import { isMac } from '../utils/platform';

const logger = createLogger('web:page:chat');

const isMobile = () => window.matchMedia('(max-width: 639px)').matches;

export const ChatPage: React.FC = () => {
  // Claim ids of the patient Real-Time Mode currently has matched on screen, so
  // the Tasks tab can pin that patient's tasks to the top. Empty unless RTM has
  // resolved a patient (financial data only exists on matched/likely states).
  const rtmMatch = useRealTimeModeMatch();
  const activePatientClaimIds =
    rtmMatch.status === 'matched' || rtmMatch.status === 'likely'
      ? rtmMatch.data.recentClaims.map(c => c.claimId).filter(Boolean)
      : [];

  const { isSidebar } = usePlatform();
  const chatViewRef = React.useRef<ChatViewRef>(null);
  // Session panel defaults to closed in the extension sidebar — the panel is narrow
  // and users want the full width for chat. They can still open it via the toolbar icon.
  const [showSidebar, setShowSidebar] = React.useState(!isSidebar);
  const [showSettingsSidebar, setShowSettingsSidebar] = React.useState(true);
  const [showBugReport, setShowBugReport] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [currentView, setCurrentView] = React.useState<'chat' | 'settings' | 'tasks'>('chat');
  const [settingsInitialSection, setSettingsInitialSection] = React.useState<
    SettingsSection | undefined
  >(undefined);
  const [showPromptForm, setShowPromptForm] = React.useState(false);
  const [editingPrompt, setEditingPrompt] = React.useState<PromptTemplate | null>(null);
  const [showShortcutsCheatSheet, setShowShortcutsCheatSheet] = React.useState(false);

  const showOnboarding = useShowOnboarding();

  // Load user prompts from server and sync with store
  usePromptLibraryIntegration();

  // Get store actions
  const addUserPrompt = usePromptLibraryStore(state => state.addUserPrompt);
  const updateUserPrompt = usePromptLibraryStore(state => state.updateUserPrompt);

  // API mutations
  const createMutation = useCreateUserPromptMutation();
  const updateMutation = useUpdateUserPromptMutation();

  const handleToggleSidebar = React.useCallback(() => {
    if (currentView === 'settings') {
      // In settings view, toggle goes back to chat
      setCurrentView('chat');
      setShowSettings(false);
    } else {
      // In chat view, toggle shows/hides sidebar
      setShowSidebar(prev => !prev);
    }
  }, [currentView]);

  const handleSettings = React.useCallback(() => {
    if (currentView === 'settings') {
      if (isSidebar) {
        // In sidebar mode there's no settings nav panel to toggle — just go back to chat
        setCurrentView('chat');
        setShowSettings(false);
      } else {
        // On web/desktop, toggle shows/hides the settings nav sidebar
        setShowSettingsSidebar(prev => !prev);
      }
    } else {
      // In chat view, open settings
      setCurrentView('settings');
      setShowSettings(true);
    }
  }, [currentView, isSidebar]);

  const handleCloseSettings = React.useCallback(() => {
    setCurrentView('chat');
    setShowSettings(false);
  }, []);

  // Open Settings on the Integrations section. Used by the McpSelect
  // "add account" button and other integrations entry points so the
  // navigation works identically on web and desktop.
  const handleGoToIntegrations = React.useCallback(() => {
    setSettingsInitialSection('integrations');
    setCurrentView('settings');
    setShowSettings(true);
  }, []);

  // Toggle the Enter Tasks worklist view (own pane, like settings).
  const handleTasks = React.useCallback(() => {
    setShowSettings(false);
    setCurrentView(prev => (prev === 'tasks' ? 'chat' : 'tasks'));
  }, []);

  // Deep link support: open Settings to a specific section when the URL carries a
  // `panel` query param (e.g. /settings?panel=admin from the extension sidebar).
  // Runs once on mount; the param is cleared afterward so a refresh doesn't re-trigger.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const panel = params.get('panel');
    if (!panel || !isSettingsSection(panel)) return;

    setSettingsInitialSection(panel);
    setCurrentView('settings');
    setShowSettings(true);

    params.delete('panel');
    const cleaned = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${cleaned ? `?${cleaned}` : ''}`
    );
  }, []);

  // Global keyboard shortcuts (new chat, cheat sheet)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const modifier = isMac() ? e.metaKey : e.ctrlKey;

      // ⌘+K / Ctrl+K — new chat from anywhere in the app
      if (modifier && e.key.toLowerCase() === 'k' && currentView === 'chat') {
        e.preventDefault();
        chatViewRef.current?.newChat();
        if (isSidebar) setShowSidebar(false);
        return;
      }

      // ⌘+/ / Ctrl+/ — open shortcuts cheat sheet
      if (modifier && e.key === '/') {
        e.preventDefault();
        setShowShortcutsCheatSheet(prev => !prev);
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentView, isSidebar]);

  const handleBugReport = React.useCallback(() => {
    setShowBugReport(true);
  }, []);

  const handleCreatePrompt = React.useCallback(() => {
    setEditingPrompt(null);
    setShowPromptForm(true);
  }, []);

  const handleEditPrompt = React.useCallback((prompt: PromptTemplate) => {
    setEditingPrompt(prompt);
    setShowPromptForm(true);
  }, []);

  const handleSavePrompt = React.useCallback(
    async (promptData: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt' | 'source'>) => {
      try {
        if (editingPrompt) {
          // Update existing prompt
          const result = await updateMutation.mutateAsync({
            params: { promptId: editingPrompt.id },
            body: promptData,
          });

          if (result?.body?.prompt) {
            updateUserPrompt(editingPrompt.id, result.body.prompt);
            toast.success('Prompt updated', `"${promptData.title}" has been updated`);
          }
        } else {
          // Create new prompt
          const result = await createMutation.mutateAsync({
            body: promptData,
          });

          if (result?.body?.prompt) {
            addUserPrompt(result.body.prompt);
            toast.success('Prompt created', `"${promptData.title}" has been added`);
          }
        }

        setShowPromptForm(false);
        setEditingPrompt(null);
      } catch (error) {
        logger.error('Failed to save prompt:', error);
        const message = error instanceof Error ? error.message : 'Could not save prompt to server';
        toast.error('Save failed', message);
      }
    },
    [editingPrompt, createMutation, updateMutation, addUserPrompt, updateUserPrompt]
  );

  if (showOnboarding) {
    return <OnboardingFlow />;
  }

  return (
    <ChatAvailabilityProvider>
      <div className="h-screen w-full flex flex-col bg-background">
        {/* Toolbar */}
        <Toolbar
          showSidebar={currentView === 'chat' ? showSidebar : showSettingsSidebar}
          onToggleSidebar={handleToggleSidebar}
          onSettings={handleSettings}
          onBugReport={handleBugReport}
          onNewChat={
            isSidebar
              ? () => {
                  chatViewRef.current?.newChat();
                  setShowSidebar(false);
                }
              : undefined
          }
          onTasks={handleTasks}
          currentView={currentView}
        />

        {/* Main Content - Show either Chat or Settings */}
        <div className="flex-1 min-h-0">
          {currentView === 'chat' ? (
            <ChatView
              ref={chatViewRef}
              onClose={() => {}}
              showSessionPanel={showSidebar}
              onSessionSelect={() => {
                if (isMobile()) setShowSidebar(false);
              }}
              onGoToIntegrations={handleGoToIntegrations}
            />
          ) : currentView === 'tasks' ? (
            <EnterTasksView
              onConnectIntegration={handleGoToIntegrations}
              activePatientClaimIds={activePatientClaimIds}
            />
          ) : (
            <SettingsView
              onClose={handleCloseSettings}
              onGoToChat={handleCloseSettings}
              showSidebar={showSettingsSidebar}
              {...(settingsInitialSection && { initialSection: settingsInitialSection })}
              onNavItemSelect={() => {
                if (isMobile()) setShowSettingsSidebar(false);
              }}
            />
          )}
        </div>

        {/* Bug Report Modal */}
        <BugReportModal isOpen={showBugReport} onClose={() => setShowBugReport(false)} />

        {/* Prompt Library Modal */}
        <PromptLibraryModal onCreatePrompt={handleCreatePrompt} onEditPrompt={handleEditPrompt} />

        {/* Prompt Form Modal */}
        <PromptFormModal
          isOpen={showPromptForm}
          onClose={() => {
            setShowPromptForm(false);
            setEditingPrompt(null);
          }}
          onSave={handleSavePrompt}
          editingPrompt={editingPrompt}
        />

        {/* Keyboard shortcuts cheat sheet (⌘+/) */}
        <ShortcutsCheatSheetModal
          isOpen={showShortcutsCheatSheet}
          onClose={() => setShowShortcutsCheatSheet(false)}
        />

        {/* One-time tip teaching users about the shortcut cheat sheet.
            Gated on onboarding so it doesn't overlap with the welcome flow. */}
        {!showOnboarding && (
          <ShortcutTipCard onOpenCheatSheet={() => setShowShortcutsCheatSheet(true)} />
        )}
      </div>
    </ChatAvailabilityProvider>
  );
};
